import { FleetLandingHeader, FleetLandingDock } from './FleetLandingNavigation';
import LandingNavIcon from './LandingNavIcon';
import { propertyFetch } from '../services/idxPropertyClient';
import { submitChatLead } from '../services/chatLeadService';
// src/components/LandingPage.js
import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ActionButtons from './ActionButtons';
import BlogsPage from './BlogsPage';
import ReviewsPage from './ReviewsPage';
import SEOHelmet from './SEOHelmet';
import { SITE_CONFIG } from '../config';
import { useTheme } from '../contexts/ThemeContext';
import './LandingPage.css';
import './IntroVideo.css';
import LandingHome from './LandingHome';
import './LandingRedesign.css';
import HoneypotField, { isHoneypotSubmission } from './HoneypotField';

import './FleetLandingNavigation.css';

const LandingPage = ({ directListing = false, directSearch = false }) => {
  const { listingKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState(null);
  const [showMapPage, setShowMapPage] = useState(false);
  const [showGlobeMap, setShowGlobeMap] = useState(directSearch || directListing || location.state?.openGlobeMap);
  const [, setShowGlobeMapResults] = useState(false);
  const [showBlogs, setShowBlogs] = useState(false);
  const [showReviews, setShowReviews] = useState(false);
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showSignInDropdown, setShowSignInDropdown] = useState(false);
  const [signInUsername, setSignInUsername] = useState('');
  const [signInPassword, setSignInPassword] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    phoneNumber: '',
    name: ''
  });
  const [showIntroVideo, setShowIntroVideo] = useState(!directListing);
  const [showMLSDisclosure, setShowMLSDisclosure] = useState(false);
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [propertiesLoading, setPropertiesLoading] = useState(true);
  const [areaLoading, setAreaLoading] = useState(false);
  const [areaError, setAreaError] = useState('');
  const [, setMobileMenuOpen] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectForm, setConnectForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    postalCode: '',
    smsConsent: false,
    serviceReq: 'buying'
  });
  const [submittingConnect, setSubmittingConnect] = useState(false);
  const [showConnectSuccess, setShowConnectSuccess] = useState(false);
  const [showHomeValueModal, setShowHomeValueModal] = useState(false);
  const [homeValueForm, setHomeValueForm] = useState({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false });
  const [submittingHomeValue, setSubmittingHomeValue] = useState(false);
  const [showHomeValueSuccess, setShowHomeValueSuccess] = useState(false);

  React.useEffect(() => {
    if (!showConnectModal && !showHomeValueModal && !showMLSDisclosure && !showSignInDropdown) return undefined;
    const previousFocus = document.activeElement;
    const dialog = document.querySelector('.ao-site [role="dialog"]') || document.querySelector('.ao-site .signin-dropdown-content');
    const focusable = () => Array.from(dialog?.querySelectorAll('button, input:not([tabindex="-1"]), select, a[href]') || []).filter(el => !el.disabled && el.getClientRects().length > 0);
    focusable()[0]?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') {
        setShowConnectModal(false);
        setShowHomeValueModal(false);
        setShowMLSDisclosure(false);
        setShowSignInDropdown(false);
      }
      if (event.key === 'Tab') {
        const controls = focusable();
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); previousFocus?.focus(); };
  }, [showConnectModal, showHomeValueModal, showMLSDisclosure, showSignInDropdown]);

  const SERVICE_AREAS = [
    { label: 'Tulsa', city: 'Tulsa', state: 'OK' },
    { label: 'Bixby', city: 'Bixby', state: 'OK' },
    { label: 'Jenks', city: 'Jenks', state: 'OK' },
    { label: 'Broken Arrow', city: 'Broken Arrow', state: 'OK' },
    { label: 'Owasso', city: 'Owasso', state: 'OK' },
    { label: 'Sapulpa', city: 'Sapulpa', state: 'OK' },
  ];

  const handleAreaSearch = async (idx) => {const area=SERVICE_AREAS[idx]; if(!area)return;
 sessionStorage.removeItem('globeMapData');
 const params=new URLSearchParams({city:area.city,state:area.state || SITE_CONFIG.STATE,location:area.city,view:'map'});
 setShowGlobeMap(true);
 navigate(`/search?${params}`);
};

  React.useEffect(() => {
    if (showIntroVideo && !showGlobeMap) {
      document.body.classList.add('intro-active');
    } else {
      document.body.classList.remove('intro-active');
    }
    return () => document.body.classList.remove('intro-active');
  }, [showIntroVideo, showGlobeMap]);

  React.useEffect(() => {
    // Hide intro video when on a direct listing route
    if (directListing && listingKey) {
      setShowIntroVideo(false);
      setShowGlobeMap(true);
    }
  }, [directListing, listingKey]);

  React.useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
  }, [isDarkMode]);

  React.useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }

    // Fetch featured properties
    const fetchFeaturedProperties = async () => {
      try {
        const [privateRes, mlsRes] = await Promise.all([
          fetch(`https://tophamtechbe.com/api/treas/private/v1/listings?site_id=${SITE_CONFIG.SITE_ID}`, {
            headers: { 'x-api-key': 'treas-api-key-2025' }
          }),
          propertyFetch(`https://tophamtechbe.com/api/treas/idx/v1/retrieve-property-summary/page?minPrice=500000&maxPrice=600000&daysonmarketSort=newold&firstPhoto=true&hasMedia=true&pageSize=10&state=${SITE_CONFIG.STATE}`, {})
        ]);

        const privateListings = privateRes.ok ? (await privateRes.json()).listings || [] : [];
        const mlsProperties = mlsRes.ok ? (await mlsRes.json()).properties || [] : [];

        const mappedPrivate = await Promise.all(privateListings.map(async l => {
          let first_photo_url = l.main_image_url || null;
          try {
            const mediaRes = await fetch(`https://tophamtechbe.com/api/treas/media/private/v1/media/${SITE_CONFIG.SITE_ID}/${l.id}`, {
              headers: { 'x-api-key': 'treas-api-key-2025' }
            });
            if (mediaRes.ok) {
              const mediaData = await mediaRes.json();
              const sorted = (mediaData.media || []).sort((a, b) => a.order_hint - b.order_hint);
              if (sorted.length > 0) first_photo_url = sorted[0].media_url || sorted[0].s3_link;
            }
          } catch (e) {}
          return {
            ...l,
            listing_key: `PRIVATE_${l.id}`,
            current_price: l.listing_type === 'sale' ? l.price_sale : l.price_lease_monthly,
            street_number: l.address_line_1?.split(' ')[0] || '',
            street_name: l.address_line_1?.split(' ').slice(1).join(' ') || '',
            first_photo_url,
            isPrivate: true
          };
        }));

        const combined = [...mappedPrivate, ...mlsProperties].slice(0, 10);
        setFeaturedProperties(combined);
      } catch (error) {
        console.error('Error fetching featured properties:', error);
      } finally {
        setPropertiesLoading(false);
      }
    };

    fetchFeaturedProperties();

    // Handle direct listing access
    if (directListing && listingKey) {
      // Store listing key for ActionButtons to use
      sessionStorage.setItem('directListingKey', listingKey);
    }

    // Track page view
    const trackPageView = async () => {
      try {
        await fetch('https://tophamtechbeb.com/aw-monitor/v1/page-views', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            site_id: SITE_CONFIG.SITE_ID,
            page_path: window.location.pathname,
            referrer: document.referrer || '',
            is_mobile: /Mobi|Android/i.test(navigator.userAgent),
            user_agent: navigator.userAgent,
            session_id: sessionStorage.getItem('session_id') || Math.random().toString(36).substr(2, 9)
          })
        });
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    };

    if (!sessionStorage.getItem('session_id')) {
      sessionStorage.setItem('session_id', Math.random().toString(36).substr(2, 9));
    }

    trackPageView();

    // Listen for globe map open event from AgentInfo
    const handleOpenGlobeMap = () => {
      setShowGlobeMap(true);
    };

    window.addEventListener('openGlobeMap', handleOpenGlobeMap);

    return () => {
      window.removeEventListener('openGlobeMap', handleOpenGlobeMap);
    };
  }, []);

  const handleToggle = () => {
    toggleTheme();
  };

  const handleSignInToggle = () => {
    setShowSignInDropdown(!showSignInDropdown);
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: signInUsername,
          password: signInPassword,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
        localStorage.setItem('user', JSON.stringify(userData));
        setShowSignInDropdown(false);
        setShowForgotPassword(false);
        setSignInUsername('');
        setSignInPassword('');
        setResetEmail('');
      } else {
        alert('Login failed. Please check your credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...registerData,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        // Auto sign in after registration
        const signInResponse = await fetch('https://tophamtechbe.com/api/treas/auth/signin', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            username: registerData.username,
            password: registerData.password,
            siteId: SITE_CONFIG.SITE_ID
          })
        });

        if (signInResponse.ok) {
          const userData = await signInResponse.json();
          setUser(userData);
          setIsAdmin(userData.roles?.includes('ROLE_ADMIN') || userData.roles?.includes('ROLE_ASSISTANT') || false);
          localStorage.setItem('user', JSON.stringify(userData));
          setShowSignInDropdown(false);
          setRegisterData({ username: '', email: '', password: '', phoneNumber: '', name: '' });
        }
      } else {
        alert('Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      alert('Registration failed. Please try again.');
    } finally {
      setSigningIn(false);
    }
  };

  const handleLogOut = () => {
    setUser(null);
    setIsAdmin(false);
    setShowSignInDropdown(false);
    localStorage.removeItem('user');
  };

  const handlePasswordResetSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('https://tophamtechbe.com/api/treas/auth/password-reset', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetEmail,
          baseUrl: window.location.origin,
          siteId: SITE_CONFIG.SITE_ID
        })
      });

      if (response.ok) {
        setResetSuccess(true);
      } else {
        alert('Failed to send reset link. Please try again.');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      alert('Failed to send reset link. Please try again.');
    }
  };

  const handleHomeValueSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowHomeValueModal(false);
      setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false});
      setShowHomeValueSuccess(true);
      setTimeout(() => setShowHomeValueSuccess(false), 6000);
      return;
    }

    setSubmittingHomeValue(true);
    try {
      const params = new URLSearchParams({
        postalCode: homeValueForm.postalCode,
        name: homeValueForm.name,
        phoneNumber: homeValueForm.phoneNumber,
        smsConsent: homeValueForm.smsConsent ? 'true' : 'false',
        serviceReq: 'estimate',
        email: homeValueForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });
      const response = await submitChatLead(new URL(`https://tophamtechbe.com/api/treas/idx/v1/chatleads?${params}`).searchParams, { rememberContact: true });
      if (response.ok) {
        setShowHomeValueModal(false);
        setHomeValueForm({ name: '', email: '', phoneNumber: '', postalCode: '', smsConsent: false});
        setShowHomeValueSuccess(true);
        setTimeout(() => setShowHomeValueSuccess(false), 6000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Home value form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingHomeValue(false);
    }
  };

  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    if (isHoneypotSubmission(e.currentTarget)) {
      setShowConnectModal(false);
      setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying', smsConsent: false});
      setShowConnectSuccess(true);
      setTimeout(() => setShowConnectSuccess(false), 5000);
      return;
    }

    setSubmittingConnect(true);
    try {
      const params = new URLSearchParams({
        postalCode: connectForm.postalCode,
        name: connectForm.name,
        phoneNumber: connectForm.phoneNumber,
        smsConsent: connectForm.smsConsent ? 'true' : 'false',
        serviceReq: connectForm.serviceReq,
        email: connectForm.email,
        siteId: SITE_CONFIG.SITE_ID
      });

      const response = await submitChatLead(new URL(`https://tophamtechbe.com/api/treas/idx/v1/chatleads?${params}`).searchParams, { rememberContact: true });

      if (response.ok) {
        setShowConnectModal(false);
        setShowConnectSuccess(true);
        setConnectForm({ name: '', email: '', phoneNumber: '', postalCode: '', serviceReq: 'buying', smsConsent: false});
        setTimeout(() => setShowConnectSuccess(false), 5000);
      } else {
        alert('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Connect form error:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setSubmittingConnect(false);
    }
  };

  return (
    <div className="landing-page ao-site">
      <SEOHelmet title={`${SITE_CONFIG.AGENT.NAME} - Real Estate Agent | ${SITE_CONFIG.AGENT.OFFICE_CITY}, ${SITE_CONFIG.STATE}`} />
      <a className="ao-skip-link" href="#ao-main">Skip to content</a>
      {!showGlobeMap && !showMapPage && <><FleetLandingHeader brand={<a className="ao-brand" href="/" aria-label="Andy Osburn home">
          <span className="ao-brand-name">Andy Osburn<span className="ao-brand-dot">.</span></span>
          <span className="ao-brand-sub">TRESCO, LLC · TULSA REAL ESTATE</span>
        </a>} onAbout={() => navigate('/agent')} onGuides={() => navigate('/communities')} guideLabel="Communities" onContact={() => setShowConnectModal(true)} onToggleTheme={handleToggle} isDarkMode={isDarkMode} /><FleetLandingDock hidden={showBlogs || showReviews || showGlobeMap || showMapPage} isDarkMode={isDarkMode} onSearch={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search'); setShowGlobeMap(true); setMobileMenuOpen(false); }} onChat={() => navigate('/chat')} onJournal={() => navigate('/blogs')} onReviews={() => navigate('/reviews')} onCloseAccount={() => setShowSignInDropdown(false)} isAdmin={isAdmin} onAdmin={() => { window.location.href = '/admin'; }}><div className="signin-dropdown"><button type="button" className="fleet-dock-button signin-toggle" onClick={handleSignInToggle} aria-expanded={showSignInDropdown} aria-haspopup="dialog"><LandingNavIcon name="account" /><span>{user ? 'Account' : 'Sign in'}</span></button>{showSignInDropdown && (
            <div className="signin-dropdown-content" role="dialog" aria-label={user ? 'Account menu' : 'Sign in'}>
              {!user ? (
                showForgotPassword ? (
                  resetSuccess ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <h3 style={{color: '#28a745', margin: '0 0 15px 0'}}>✓ Email Sent!</h3>
                      <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>Please check your email for password reset instructions.</p>
                      <button 
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); setResetSuccess(false); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  ) : (
                    <div>
                      <form onSubmit={handlePasswordResetSubmit} className="signin-form">
                        <h3>Reset Password</h3>
                        <input
                          type="email"
                          aria-label="Enter your email" placeholder="Enter your email"
                          value={resetEmail}
                          onChange={(e) => setResetEmail(e.target.value)}
                          required
                        />
                        <button type="submit">
                          Send Reset Link
                        </button>
                      </form>
                      <button 
                        onClick={() => { setShowForgotPassword(false); setResetEmail(''); }}
                        className="toggle-form-btn"
                      >
                        Back to Sign In
                      </button>
                    </div>
                  )
                ) : !showRegisterForm ? (
                  <div>
                    <form onSubmit={handleSignIn} className="signin-form">
                      <h3>Sign In</h3>
                      <input
                        type="text"
                        aria-label="Username or Email" placeholder="Username or Email"
                        value={signInUsername}
                        onChange={(e) => setSignInUsername(e.target.value)}
                        required
                      />
                      <input
                        type="password"
                        aria-label="Password" placeholder="Password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Signing In...' : 'Sign In'}
                      </button>
                    </form>
                    <button 
                      onClick={() => setShowRegisterForm(true)}
                      className="toggle-form-btn"
                    >
                      Need an account? Register
                    </button>
                    <button 
                      type="button" 
                      onClick={() => setShowForgotPassword(true)}
                      style={{background: 'none', border: 'none', color: '#666', fontSize: '11px', textDecoration: 'underline', cursor: 'pointer', marginTop: '12px', padding: '4px 0'}}
                    >
                      Forgot Password?
                    </button>
                  </div>
                ) : (
                  <div>
                    <form onSubmit={handleRegister} className="signin-form">
                      <h3>Register</h3>
                      <input
                        type="text"
                        aria-label="Username" placeholder="Username"
                        value={registerData.username}
                        onChange={(e) => setRegisterData({...registerData, username: e.target.value})}
                        required
                      />
                      <input
                        type="email"
                        aria-label="Email" placeholder="Email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        required
                      />
                      <input
                        type="password"
                        aria-label="Password" placeholder="Password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        required
                      />
                      <input
                        type="tel"
                        aria-label="Phone Number" placeholder="Phone Number"
                        value={registerData.phoneNumber}
                        onChange={(e) => setRegisterData({...registerData, phoneNumber: e.target.value})}
                        required
                      />
                      <input
                        type="text"
                        aria-label="Full Name" placeholder="Full Name"
                        value={registerData.name}
                        onChange={(e) => setRegisterData({...registerData, name: e.target.value})}
                        required
                      />
                      <button type="submit" disabled={signingIn}>
                        {signingIn ? 'Registering...' : 'Register'}
                      </button>
                    </form>
                    <button 
                      onClick={() => setShowRegisterForm(false)}
                      className="toggle-form-btn"
                    >
                      Have an account? Sign In
                    </button>
                  </div>
                )
              ) : (
                <div className="user-info">
                  <h3>User Info</h3>
                  <p><strong>Name:</strong> {user.username}</p>
                  <p><strong>Email:</strong> {user.email}</p>
                  <p><strong>Role:</strong> {user.roles?.join(', ')}</p>
                  <button onClick={handleLogOut} className="logout-btn">
                    Log Off
                  </button>
                </div>
              )}
            </div>
          )}</div></FleetLandingDock></>}
      <div className="fullscreen-chat-container">
        {showIntroVideo && !showGlobeMap && (
          <LandingHome
            areas={SERVICE_AREAS}
            onAreaSearch={handleAreaSearch}
            areaLoading={areaLoading}
            areaError={areaError}
            onSearch={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/search');setShowGlobeMap(true);}}
            onConnect={() => setShowConnectModal(true)}
            onHomeValue={() => setShowHomeValueModal(true)}
            properties={featuredProperties}
            propertiesLoading={propertiesLoading}
          />
        )}
        {!showIntroVideo && !showGlobeMap && <div className="chat-wrapper fullscreen">
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
            hidePrompts={!showIntroVideo}
          />
        </div>}
      </div>

      {showMapPage && (
        <div className="full-map-page">
          <button 
            className="close-map-page"
            onClick={() => setShowMapPage(false)}
          >
            ← Back to Chat
          </button>
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            isMapPage={true}
            onMapPageClose={() => setShowMapPage(false)}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showGlobeMap && (
        <div className="full-map-page">
          <ActionButtons 
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            isGlobeMap={true}
            onGlobeMapResultsChange={setShowGlobeMapResults}
            onGlobeMapClose={() => {sessionStorage.removeItem('globeMapData');window.history.replaceState(window.history.state,'','/');
              setShowGlobeMap(false);
              setShowGlobeMapResults(false);
              setShowIntroVideo(true);
            }}
            directListingKey={directListing ? listingKey : null}
            user={user}
            isAdmin={isAdmin}
            setUser={setUser}
            setIsAdmin={setIsAdmin}
          />
        </div>
      )}
      {showBlogs && (
        <BlogsPage onClose={() => setShowBlogs(false)} />
      )}
      {showReviews && (
        <ReviewsPage onClose={() => setShowReviews(false)} />
      )}

      {showIntroVideo && !showGlobeMap && <footer className="ao-footer">
        <div className="ao-footer-top">
          <div><a className="ao-brand-name" href="/">Andy Osburn.</a><p>TRESCO, LLC · Tulsa Real Estate</p></div>
          <div><a href={`tel:${SITE_CONFIG.AGENT.PHONE.replace(/[^+\d]/g, '')}`}>{SITE_CONFIG.AGENT.PHONE}</a><a href={`mailto:${SITE_CONFIG.AGENT.EMAIL}`}>{SITE_CONFIG.AGENT.EMAIL}</a></div>
          <div><a href="/communities">Tulsa-area community guides</a><a href="/neighborhood/idxtown">Explore IDXTown</a><a href="/contact-us">Contact Andy</a></div>
        </div>
        <div className="ao-footer-bottom">
          <span>© {new Date().getFullYear()} {SITE_CONFIG.AGENT.NAME}. All rights reserved.</span>
          <div><a href="/privacy-policy">Privacy Policy</a><a href="/terms-of-service">Terms of Service</a><button onClick={() => setShowMLSDisclosure(true)}>MLS Disclosure</button></div>
          <a href="https://agentwinds.com" target="_blank" rel="noopener noreferrer"><img src="https://aw-business-marketing.s3.us-east-1.amazonaws.com/websitesbyaw-transparent.png" alt="Websites by Agent Winds" /></a>
        </div>
      </footer>}

      {showMLSDisclosure && (
        <div className="modal-overlay" onClick={() => setShowMLSDisclosure(false)}>
          <div className="mls-disclosure-modal" role="dialog" aria-modal="true" aria-label="MLS Disclosure" onClick={(e) => e.stopPropagation()}>
            <h3>MLS Disclosure</h3>
            <p>
              but not guaranteed. All properties are subject to prior sale, change or withdrawal. 
              Neither listing broker(s) or information provider(s) shall be responsible for any typographical errors, 
              misinformation, misprints and shall be held totally harmless. Listing(s) information is provided for 
              consumer's personal, non-commercial use and may not be used for any purpose other than to identify 
              prospective properties consumers may be interested in purchasing. The data relating to real estate for 
              sale on this website comes in part from the Internet Data Exchange program of the Multiple Listing Service. 
              Real estate listings held by brokerage firms other than Providence Realty may be marked with the Internet 
              Data Exchange logo and detailed information about those properties will include the name of the listing 
              broker(s) when required by the MLS. Copyright ©2025 All rights reserved. Last Updated: December 20, 2025 7:58 PM UTC
            </p>
            <button onClick={() => setShowMLSDisclosure(false)} className="close-disclosure-btn">
              Close
            </button>
          </div>
        </div>
      )}

      {showConnectSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Thank you! We'll be in touch soon.</span>
          </div>
        </div>
      )}

      {showHomeValueSuccess && (
        <div className="connect-success-notification">
          <div className="connect-success-content">
            <span className="connect-success-icon">✓</span>
            <span className="connect-success-text">Got it! Andy will personally reach out to provide your free home evaluation.</span>
          </div>
        </div>
      )}

      {showHomeValueModal && (
        <div className="modal-overlay" onClick={() => setShowHomeValueModal(false)}>
          <div className="connect-modal" role="dialog" aria-modal="true" aria-label="Free Home Value Estimate" onClick={(e) => e.stopPropagation()}>
            <h3>Free Home Value Estimate</h3>
            <form onSubmit={handleHomeValueSubmit}>
          <HoneypotField />
              <input type="text" aria-label="Full Name" placeholder="Full Name" value={homeValueForm.name} onChange={(e) => setHomeValueForm({...homeValueForm, name: e.target.value})} required />
              <input type="email" aria-label="Email" placeholder="Email" value={homeValueForm.email} onChange={(e) => setHomeValueForm({...homeValueForm, email: e.target.value})} required />
              <input type="tel" aria-label="Phone Number" placeholder="Phone Number" value={homeValueForm.phoneNumber} onChange={(e) => setHomeValueForm({...homeValueForm, phoneNumber: e.target.value})} required />
              <input type="text" aria-label="ZIP Code" placeholder="ZIP Code" value={homeValueForm.postalCode} onChange={(e) => setHomeValueForm({...homeValueForm, postalCode: e.target.value})} required />
              <label className="sms-consent-control">
                <input type="checkbox" checked={homeValueForm.smsConsent} onChange={(e) => setHomeValueForm({...homeValueForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <button type="submit" disabled={submittingHomeValue}>{submittingHomeValue ? 'Submitting...' : 'Submit'}</button>
              <button type="button" onClick={() => setShowHomeValueModal(false)} className="cancel-btn">Cancel</button>
            </form>
          </div>
        </div>
      )}

      {showConnectModal && (
        <div className="modal-overlay" onClick={() => setShowConnectModal(false)}>
          <div className="connect-modal" role="dialog" aria-modal="true" aria-label="Contact Andy" onClick={(e) => e.stopPropagation()}>
            <h3>Ready to Connect?</h3>
            <form onSubmit={handleConnectSubmit}>
          <HoneypotField />
              <input
                type="text"
                aria-label="Full Name" placeholder="Full Name"
                value={connectForm.name}
                onChange={(e) => setConnectForm({...connectForm, name: e.target.value})}
                required
              />
              <input
                type="email"
                aria-label="Email" placeholder="Email"
                value={connectForm.email}
                onChange={(e) => setConnectForm({...connectForm, email: e.target.value})}
                required
              />
              <input
                type="tel"
                aria-label="Phone Number" placeholder="Phone Number"
                value={connectForm.phoneNumber}
                onChange={(e) => setConnectForm({...connectForm, phoneNumber: e.target.value})}
                required
              />
              <input
                type="text"
                aria-label="ZIP Code" placeholder="ZIP Code"
                value={connectForm.postalCode}
                onChange={(e) => setConnectForm({...connectForm, postalCode: e.target.value})}
                required
              />
              <label className="sms-consent-control">
                <input type="checkbox" checked={connectForm.smsConsent} onChange={(e) => setConnectForm({...connectForm, smsConsent: e.target.checked})} />
                <span>I agree to receive text messages about this request. Consent is not a condition of service. Message and data rates may apply; reply STOP to opt out.</span>
              </label>
              <select
                aria-label="How can Andy help?"
                value={connectForm.serviceReq}
                onChange={(e) => setConnectForm({...connectForm, serviceReq: e.target.value})}
                required
              >
                <option value="buying">Buying</option>
                <option value="selling">Selling</option>
                <option value="both">Both</option>
              </select>
              <button type="submit" disabled={submittingConnect}>
                {submittingConnect ? 'Submitting...' : 'Submit'}
              </button>
              <button type="button" onClick={() => setShowConnectModal(false)} className="cancel-btn">
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default LandingPage;
